import isPlainObject from 'lodash/isPlainObject';
import mapValues from 'lodash/mapValues';

/**
 * Helpers that expose a color tree (MUI palette, legacy `colors`) as CSS custom properties.
 *
 * Static imports (`THEME.palette.*`, `colors.*`) resolve to `var(--fp-..., <light value>)` strings,
 * so they follow the active theme without a reload. The real MUI theme handed to
 * `MuiThemeProvider` keeps plain color values because MUI color helpers cannot parse `var()`.
 */

/** Matches one whole token built by `createCssVarTokens`: `var(--fp-<path>, <fallback>)`. */
const CSS_VAR_TOKEN = /^var\((--fp-[\w-]+), (.+)\)$/;

const cssVarName = path => `--fp-${path.join('-')}`;

/** `palette.type` (`'light'`/`'dark'`) is a string but not a color. */
const isColor = (key, value) => typeof value === 'string' && key !== 'type';

/**
 * Returns a copy of `tree` where every color string becomes `var(--fp-<path>, <value>)`.
 * Non-color values (numbers, functions, `type`) are kept as they are.
 * @param {Object} tree
 * @param {Array<string>} path
 * @returns {Object}
 */
export const createCssVarTokens = (tree, path) =>
  mapValues(tree, (value, key) => {
    const keyPath = [...path, key];
    if (isPlainObject(value)) return createCssVarTokens(value, keyPath);
    return isColor(key, value) ? `var(${cssVarName(keyPath)}, ${value})` : value;
  });

const colorEntries = (tree, path) =>
  Object.entries(tree).flatMap(([key, value]) => {
    const keyPath = [...path, key];
    if (isPlainObject(value)) return colorEntries(value, keyPath);
    return isColor(key, value) ? [[cssVarName(keyPath), value]] : [];
  });

/**
 * Flattens every color string of `tree` into `{ '--fp-<path>': value }`.
 * @param {Object} tree
 * @param {Array<string>} path
 * @returns {Object<string, string>}
 */
export const flattenCssVars = (tree, path) => Object.fromEntries(colorEntries(tree, path));

/**
 * Replaces every theme token in `value` (deeply, for plain objects and arrays) with the concrete
 * color from `vars`, or the token's light fallback when the variable is unknown. Use it for
 * consumers that cannot read CSS variables, such as Google Charts or canvas APIs. Dates and other
 * class instances are returned unchanged.
 * @param {*} value
 * @param {Object<string, string>} vars map from `getThemeCssVars`
 * @returns {*}
 */
export const resolveCssVars = (value, vars) => {
  if (Array.isArray(value)) return value.map(item => resolveCssVars(item, vars));
  if (isPlainObject(value)) return mapValues(value, item => resolveCssVars(item, vars));
  if (typeof value !== 'string') return value;

  const [, name, fallback] = value.match(CSS_VAR_TOKEN) ?? [];
  return name ? vars[name] ?? fallback : value;
};
